import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { MessageSchema } from "./llm-messages";

// Types duplicated from redo/agents to avoid circular dependency
// These should stay in sync with the canonical definitions in redo/agents
export type ToolChoice =
  | "auto"
  | "none"
  | "required"
  | { type: "tool"; toolName: string };

// Simplified model params for serialization (subset of ModelParams from redo/agents)
// This mirrors the structure but only includes serializable fields
export interface ModelParamsForSerialization {
  provider: "openai" | "google" | "anthropic" | "xai";
  model: string;
}

const ModelParamsSchema_ = z.object({
  provider: createEnum(["openai", "google", "anthropic", "xai"], {
    enumName: "LlmProvider",
  }),
  model: z.string(),
});
export const ModelParamsSchema: z.ZodType<ModelParamsForSerialization> =
  ModelParamsSchema_;

// Tool schema for serialization (simplified version of ToolDefinition from redo/agents)
// Contains only the fields needed for serialization/storage
export interface ToolForSerialization {
  name: string;
  description: string;
  parametersSchema: unknown; // Serialized Zod schema
}

const ToolSchema_ = z.object({
  name: z.string(),
  description: z.string(),
  parametersSchema: z.unknown(), // Serialized Zod schema
});
export const ToolSchema: z.ZodType<ToolForSerialization> = ToolSchema_;

// Zod schema for ToolChoice
const ToolChoiceSchema_ = z.union([
  z.literal("auto"),
  z.literal("required"),
  z.literal("none"),
  z.object({ type: z.literal("tool"), toolName: z.string() }),
]);
export const ToolChoiceSchema: z.ZodType<ToolChoice> = ToolChoiceSchema_;

const LlmInitializationInputSchema_ = z.object({
  _id: zExt.objectId(),

  // Agent Identity
  agentName: z.string(),
  agentGroup: z.string(), // AgentGroup enum values as strings
  versionedAgentId: zExt.objectId().nullish(),

  // Model Configuration
  modelParams: ModelParamsSchema,

  // Agent Definition Schema (serialized as JSON)
  outputSchema: z.unknown().optional(), // For StrictObjectAgent only

  // Initialization Data
  input: z.unknown(), // Input data used to initialize agent
  initialState: z.unknown(), // State returned by agent.initialize()
  prompt: z.string(), // System prompt
  initialMessages: z.array(MessageSchema), // Initial conversation messages

  // Tools (for ToolCallingAgent)
  tools: z.array(ToolSchema).nullish(),
  toolChoice: ToolChoiceSchema.nullish(),

  // Context
  team: zExt.objectId(),
  threadId: zExt.objectId(), // For backlinking to the thread this was captured from
  parentThreadId: zExt.objectId().nullish(),

  // Metadata
  createdAt: z.date(),
});
export const LlmInitializationInputSchema: z.ZodType<LlmInitializationInput> =
  LlmInitializationInputSchema_;
export interface LlmInitializationInput
  extends z.infer<typeof LlmInitializationInputSchema_> {}
